import { useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

const DANGEROUS_CHAR_REGEX = /[<>]/g;
const WARNING_MESSAGE = 'Special characters < > are not allowed in this field.';
const TOAST_DEBOUNCE_MS = 2000;

const TEXT_INPUT_TYPES = new Set([
	'text',
	'email',
	'tel',
	'search',
	'url',
	'password',
]);

function isProtectedInput(target: EventTarget | null): target is HTMLInputElement {
	if (!(target instanceof HTMLInputElement)) return false;
	if (target.dataset.allowHtml !== undefined) return false;
	if (target.dataset.passwordGuard !== undefined) return false;
	const type = target.type.toLowerCase();
	return TEXT_INPUT_TYPES.has(type);
}

export function useXssInputProtection(): void {
	const lastToastTime = useRef(0);

	const showWarning = () => {
		const now = Date.now();
		if (now - lastToastTime.current > TOAST_DEBOUNCE_MS) {
			lastToastTime.current = now;
			toast.error(WARNING_MESSAGE, { autoClose: 5000, toastId: 'xss-input-warning' });
		}
	};

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (!isProtectedInput(event.target)) return;
			if (event.key === '<' || event.key === '>') {
				event.preventDefault();
				showWarning();
			}
		};

		const handlePaste = (event: ClipboardEvent) => {
			if (!isProtectedInput(event.target)) return;

			const raw = event.clipboardData?.getData('text') ?? '';
			if (!DANGEROUS_CHAR_REGEX.test(raw)) return;

			event.preventDefault();
			const cleaned = raw.replaceAll(DANGEROUS_CHAR_REGEX, '');
			const input = event.target;
			const start = input.selectionStart ?? input.value.length;
			const end = input.selectionEnd ?? start;
			const before = input.value.slice(0, start);
			const after = input.value.slice(end);
			const valueDescriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
			valueDescriptor?.set?.call(input, before + cleaned + after);
			input.dispatchEvent(new Event('input', { bubbles: true }));
			showWarning();
		};

		const handleDrop = (event: DragEvent) => {
			if (!isProtectedInput(event.target)) return;

			const raw = event.dataTransfer?.getData('text') ?? '';
			if (!DANGEROUS_CHAR_REGEX.test(raw)) return;

			event.preventDefault();
			const input = event.target;
			const cleaned = raw.replaceAll(DANGEROUS_CHAR_REGEX, '');
			const start = input.selectionStart ?? input.value.length;
			const end = input.selectionEnd ?? start;
			const before = input.value.slice(0, start);
			const after = input.value.slice(end);
			const valueDescriptor = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
			valueDescriptor?.set?.call(input, before + cleaned + after);
			input.dispatchEvent(new Event('input', { bubbles: true }));
			showWarning();
		};

		document.addEventListener('keydown', handleKeyDown, true);
		document.addEventListener('paste', handlePaste, true);
		document.addEventListener('drop', handleDrop, true);

		return () => {
			document.removeEventListener('keydown', handleKeyDown, true);
			document.removeEventListener('paste', handlePaste, true);
			document.removeEventListener('drop', handleDrop, true);
		};
	}, []);
}
